import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, CellValueChangedEvent, CellClickedEvent, CellDoubleClickedEvent } from "ag-grid-community";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import IngredientPicker from "./IngredientSearchEditor";
import FormulaReport from "./FormulaReport";
import { logActivity } from "./activityLog";
import { useTranslation } from 'react-i18next';

ModuleRegistry.registerModules([AllCommunityModule]);

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface Ingredient {
  id: string;
  common_name: string;
  cost_per_kg: number | null;
  moisture: number | null;
  supplier: string | null;
  protein_per_100g: number | null;
  fat: number | null;
  satfat: number | null;
  transfat: number | null;
  cholesterol: number | null;
  carbs: number | null;
  fiber: number | null;
  sugar: number | null;
  sodium: number | null;
  vitamin_d: number | null;
  calcium: number | null;
  iron: number | null;
  potassium: number | null;
  aa_his: number | null;
  aa_ile: number | null;
  aa_leu: number | null;
  aa_lys: number | null;
  aa_mc: number | null;
  aa_pt: number | null;
  aa_thr: number | null;
  aa_trp: number | null;
  aa_val: number | null;
  [key: string]: any;
}

interface FormulaRow {
  _rowId: string;
  id?: number;
  formula_code: string;
  component: string;
  ingredient_id: string | null;
  pct_of_component: number | null;
  display_order: number;
  row_type: string;
  grams: number | null;
  pct_of_formula: number | null;
  pct_component_display: number | null;
  pct_composite_display: number | null;
  supplier: string | null;
  lot_code: string | null;
  moisture_pct: number | null;
  cost_per_kg: number | null;
  cost_contribution: number | null;
  notes: string | null;
  process_step: string | null;
  process_instruction: string | null;
  common_name: string;
  _isPinned?: boolean;
  _fauxPct?: number | null;
}

interface FormulaHeader {
  formula_code: string;
  display_name: string | null;
  piece_weight_g: number | null;
  serving_size_g: number | null;
  dough_ratio_pct: number | null;
  owner_name: string | null;
  ratio_mode: string | null;
  final_moisture_mode: string | null;
  final_moisture_whole: number | null;
  final_moisture_dough: number | null;
  final_moisture_filling: number | null;
}

interface Props {
  formulaCode: string | null;
  onBack: () => void;
  onNavigateToFormula?: (code: string) => void;
  supabase: any;
}

/* ── Notes delimiters ─────────────────────────────────────────────────────── */
const PROC_DELIM = "\n\nPROCESS: ";
const OBS_DELIM = "\n\nOBSERVATIONS: ";

function parseNotes(raw: string | null): { formula: string; process: string; observations: string } {
  if (!raw) return { formula: "", process: "", observations: "" };
  let s = raw;
  let observations = "";
  const oi = s.indexOf(OBS_DELIM);
  if (oi >= 0) { observations = s.slice(oi + OBS_DELIM.length); s = s.slice(0, oi); }
  let process = "";
  const pi = s.indexOf(PROC_DELIM);
  if (pi >= 0) { process = s.slice(pi + PROC_DELIM.length); s = s.slice(0, pi); }
  return { formula: s.startsWith("FORMULA: ") ? s.slice(9) : s, process, observations };
}

function combineNotes(formula: string, process: string, observations: string): string {
  let s = "FORMULA: " + formula;
  if (process) s += PROC_DELIM + process;
  if (observations) s += OBS_DELIM + observations;
  return s;
}

/* ── Helpers ───────────────────────────────────────────────────────────────── */

let _rid = 0;
function rid(): string { return "r" + (++_rid); }

function tryEvalExpr(raw: string): number | null {
  const s = raw.replace(/^=/, "").trim();
  if (!/^[\d.+\-*/() ]+$/.test(s)) return null;
  try {
    const v = new Function("return (" + s + ")")();
    return typeof v === "number" && isFinite(v) ? v : null;
  } catch { return null; }
}

/**
 * Derive percentage display columns and cost from grams.
 * % w/w Component = row grams / section total grams (always sums to 100%)
 * % w/w Composite = pct_of_component * (section_ratio from dough_ratio metadata)
 */
function deriveDisplayFields(
  allRows: FormulaRow[],
  ingMap: Map<string, Ingredient>,
  doughRatioPct: number,
): FormulaRow[] {
  const sectionGrams: Record<string, number> = {};
  for (const r of allRows) {
    sectionGrams[r.component] = (sectionGrams[r.component] ?? 0) + (r.grams ?? 0);
  }

  return allRows.map((r) => {
    const ing = r.ingredient_id ? ingMap.get(r.ingredient_id) : undefined;
    const g = r.grams ?? 0;
    const secTotal = sectionGrams[r.component] ?? 0;
    const pctComp = secTotal > 0 ? g / secTotal : null;
    // Composite = pct_of_component * section_ratio
    const sectionRatio = r.component === "DOUGH" ? doughRatioPct / 100 : (100 - doughRatioPct) / 100;
    const pctComposite = pctComp != null ? pctComp * sectionRatio : null;
    const costKg = ing?.cost_per_kg ?? r.cost_per_kg;
    const costContrib = g > 0 && costKg != null ? (g / 1000) * costKg : null;
    return {
      ...r,
      pct_component_display: pctComp,
      pct_composite_display: pctComposite,
      cost_per_kg: costKg ?? null,
      cost_contribution: costContrib,
      common_name: ing?.common_name ?? r.common_name ?? "",
      moisture_pct: ing?.moisture ?? r.moisture_pct,
    };
  });
}

const NUTRI_COLS: { key: string; label: string }[] = [
  { key: "protein_per_100g", label: "Protein g/100g" },
  { key: "fat", label: "Fat g/100g" },
  { key: "satfat", label: "Sat Fat g/100g" },
  { key: "transfat", label: "Trans Fat g/100g" },
  { key: "cholesterol", label: "Cholesterol mg" },
  { key: "carbs", label: "Carbs g/100g" },
  { key: "fiber", label: "Fiber g/100g" },
  { key: "sugar", label: "Sugar g/100g" },
  { key: "sodium", label: "Sodium mg" },
  { key: "vitamin_d", label: "Vitamin D mcg" },
  { key: "calcium", label: "Calcium mg" },
  { key: "iron", label: "Iron mg" },
  { key: "potassium", label: "Potassium mg" },
  { key: "aa_his", label: "AA His mg/g" },
  { key: "aa_ile", label: "AA Ile mg/g" },
  { key: "aa_leu", label: "AA Leu mg/g" },
  { key: "aa_lys", label: "AA Lys mg/g" },
  { key: "aa_mc", label: "AA M+C mg/g" },
  { key: "aa_pt", label: "AA P+T mg/g" },
  { key: "aa_thr", label: "AA Thr mg/g" },
  { key: "aa_trp", label: "AA Trp mg/g" },
  { key: "aa_val", label: "AA Val mg/g" },
];

/* ── Cell comments ─────────────────────────────────────────────────────── */

interface CellComment {
  id: number;
  formula_code: string;
  field_name: string;
  row_id: string | null;
  comment_text: string;
  author_email: string;
  created_at: string;
}

function authorAbbrev(email: string): string {
  const local = email.split("@")[0] || "";
  const parts = local.split(".");
  return parts.map((p) => p.charAt(0).toLowerCase()).join("");
}

function commentKey(fieldName: string, rowId: string | null): string {
  return fieldName + "|" + (rowId ?? "");
}

// Key comments by the stable formula_components.id when present; unsaved rows
// fall back to a formula-scoped key from section + display_order, which is
// stable across loads (unlike the ephemeral _rowId counter that restarts each
// session and reattached persisted comments to arbitrary rows).
function commentRowKey(row: FormulaRow): string {
  if (row.id != null) return "c" + row.id;
  return "new:" + row.formula_code + ":" + row.component + ":" + row.display_order;
}

const DEFAULT_HIDDEN = new Set([
  "row_type", "supplier", "lot_code", "moisture_pct", "process_step", "process_instruction",
  "cost_per_kg", "cost_contribution",
  ...NUTRI_COLS.map((c) => "nutri_" + c.key),
]);

/* ── Component ─────────────────────────────────────────────────────────────── */

export default function FormulaSheet({ formulaCode, onBack, onNavigateToFormula, supabase }: Props) {
  const { t, i18n } = useTranslation();
  const isNew = formulaCode === null;

  const [header, setHeader] = useState<FormulaHeader>({
    formula_code: formulaCode ?? "(new)",
    display_name: isNew ? "" : null,
    piece_weight_g: isNew ? 120 : null,
    serving_size_g: isNew ? 120 : null,
    dough_ratio_pct: isNew ? 50 : null,
    owner_name: null,
    ratio_mode: "as_mixed",
    final_moisture_mode: null,
    final_moisture_whole: null,
    final_moisture_dough: null,
    final_moisture_filling: null,
  });
  const [rawRows, setRawRows] = useState<FormulaRow[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [notFound, setNotFound] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [formulaNotes, setFormulaNotes] = useState("");
  const [processNotes, setProcessNotes] = useState("");
  const [observationNotes, setObservationNotes] = useState("");
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set(DEFAULT_HIDDEN));
  const [comments, setComments] = useState<CellComment[]>([]);
  const [commentPopover, setCommentPopover] = useState<{
    x: number; y: number; fieldName: string; rowId: string | null;
  } | null>(null);
  const [newCommentText, setNewCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Import-preview request sequence: a slow response for an earlier selection
  // must not overwrite the preview of the formula selected after it.
  const importPreviewSeq = useRef(0);
  const [pickerState, setPickerState] = useState<{
    rowId: string;
    position: { top: number; left: number };
  } | null>(null);

  // Import-component modal state
  const [importModal, setImportModal] = useState<{
    targetComponent: "DOUGH" | "FILLING";
  } | null>(null);
  const [importFormulas, setImportFormulas] = useState<{ formula_code: string; display_name: string | null }[]>([]);
  const [importSearch, setImportSearch] = useState("");
  const [importSelectedCode, setImportSelectedCode] = useState<string | null>(null);
  const [importSourceComponent, setImportSourceComponent] = useState<"DOUGH" | "FILLING">("DOUGH");
  const [importPreviewRows, setImportPreviewRows] = useState<FormulaRow[]>([]);
  const [importLoading, setImportLoading] = useState(false);

  // Report generation state
  const [reportExportType, setReportExportType] = useState<"pdf" | "ppt" | null>(null);
  const [printMenuOpen, setPrintMenuOpen] = useState(false);

  // Batch mode state (per-section, in-session only — not persisted)
  const [doughBatchOn, setDoughBatchOn] = useState(false);
  const [fillingBatchOn, setFillingBatchOn] = useState(false);
  const [doughBatchSize, setDoughBatchSize] = useState<number>(500);
  const [fillingBatchSize, setFillingBatchSize] = useState<number>(500);
  const [doughInputMode, setDoughInputMode] = useState<"g" | "pct">("g");
  const [fillingInputMode, setFillingInputMode] = useState<"g" | "pct">("g");

  // Fetch current user email for comment ownership
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      setCurrentUserEmail(data?.user?.email ?? null);
    })();
  }, [supabase]);

  const ingMap = useMemo(() => {
    const m = new Map<string, Ingredient>();
    for (const i of ingredients) m.set(i.id, i);
    return m;
  }, [ingredients]);

  const doughRatio = header.dough_ratio_pct ?? 100;

  // Derived rows: raw rows + computed display fields
  const rows = useMemo(() => deriveDisplayFields(rawRows, ingMap, doughRatio), [rawRows, ingMap, doughRatio]);

  /* ── Load data ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const iRes = await supabase.from("ingredients").select("*").eq("is_active", true).order("common_name");
      if (cancelled) return;
      if (iRes.error) {
        console.error("Failed to load ingredients:", iRes.error.message);
        setSaveMsg({ type: "err", text: t('formulaSheet.failedLoadIngredients') + " " + iRes.error.message });
      }
      const ings: Ingredient[] = iRes.data ?? [];
      setIngredients(ings);
      const iMap = new Map<string, Ingredient>();
      for (const i of ings) iMap.set(i.id, i);

      if (isNew) { setLoading(false); return; }

      const [fRes, cRes] = await Promise.all([
        supabase.from("formulas").select("*").eq("formula_code", formulaCode).single(),
        supabase.from("formula_components").select("*").eq("formula_code", formulaCode).order("display_order"),
      ]);
      if (cancelled) return;

      if (fRes.error || !fRes.data) {
        if (fRes.error) console.error("Failed to load formula:", fRes.error.message);
        setNotFound(true);
      } else if (fRes.data) {
        setHeader({
          formula_code: fRes.data.formula_code,
          display_name: fRes.data.display_name,
          piece_weight_g: fRes.data.piece_weight_g,
          serving_size_g: fRes.data.serving_size_g,
          dough_ratio_pct: fRes.data.dough_ratio_pct,
          owner_name: fRes.data.owner_name,
          ratio_mode: fRes.data.ratio_mode ?? "as_mixed",
          final_moisture_mode: fRes.data.final_moisture_mode ?? null,
          final_moisture_whole: fRes.data.final_moisture_whole ?? null,
          final_moisture_dough: fRes.data.final_moisture_dough ?? null,
          final_moisture_filling: fRes.data.final_moisture_filling ?? null,
        });
        const parsed = parseNotes(fRes.data.notes);
        setFormulaNotes(parsed.formula);
        setProcessNotes(parsed.process);
        setObservationNotes(parsed.observations);

        const filteredRows = (cRes.data ?? []).filter((r: any) => (r.row_type ?? "ingredient") !== "process");

        // First pass: find known grams per section + known pct sum
        const sectionKnownGrams: Record<string, number> = {};
        const sectionKnownPct: Record<string, number> = {};
        for (const r of filteredRows) {
          const comp = r.component;
          if (r.grams != null) {
            sectionKnownGrams[comp] = (sectionKnownGrams[comp] ?? 0) + r.grams;
          }
          if (r.grams != null && r.pct_of_component != null) {
            sectionKnownPct[comp] = (sectionKnownPct[comp] ?? 0) + r.pct_of_component;
          }
        }

        const loaded: FormulaRow[] = filteredRows.map((r: any) => {
            const ing = r.ingredient_id ? iMap.get(r.ingredient_id) : undefined;
            let grams: number | null = r.grams;
            if (grams == null && r.pct_of_component != null) {
              const knownG = sectionKnownGrams[r.component];
              const knownP = sectionKnownPct[r.component];
              if (knownG != null && knownP != null && knownP > 0) {
                // Derive from known rows: grams = pct * (known_grams / known_pct_sum)
                grams = r.pct_of_component * (knownG / knownP);
              } else {
                // No known grams in section: assume 500g total
                grams = r.pct_of_component * 500;
              }
            }
            return {
              _rowId: rid(),
              id: r.id,
              formula_code: r.formula_code,
              component: r.component,
              ingredient_id: r.ingredient_id,
              pct_of_component: r.pct_of_component,
              display_order: r.display_order,
              row_type: "ingredient",
              grams,
              pct_of_formula: r.pct_of_formula,
              pct_component_display: null,
              pct_composite_display: null,
              supplier: r.supplier ?? ing?.supplier ?? null,
              lot_code: r.lot_code,
              moisture_pct: ing?.moisture ?? null,
              cost_per_kg: ing?.cost_per_kg ?? null,
              cost_contribution: null,
              notes: r.notes,
              process_step: r.process_step,
              process_instruction: r.process_instruction,
              common_name: ing?.common_name ?? "",
            };
          });
        setRawRows(loaded);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [formulaCode, supabase, isNew, t]);

  /* ── Load comments ── */
  useEffect(() => {
    if (isNew || !formulaCode) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("formula_comments")
        .select("*")
        .eq("formula_code", formulaCode)
        .order("created_at", { ascending: true });
      if (!cancelled) setComments((data as CellComment[]) ?? []);
    })();
    return () => { cancelled = true; };
  }, [formulaCode, supabase, isNew]);

  /* ── Comment helpers ── */
  const commentsByKey = useMemo(() => {
    const m = new Map<string, CellComment[]>();
    for (const c of comments) {
      const k = commentKey(c.field_name, c.row_id);
      const arr = m.get(k) ?? [];
      arr.push(c);
      m.set(k, arr);
    }
    return m;
  }, [comments]);

  const handleCellDoubleClicked = useCallback((e: CellDoubleClickedEvent<FormulaRow>) => {
    const rowData = e.data;
    if (!rowData || rowData._isPinned) return;
    const colId = e.column?.getColId?.() ?? "";
    // Stop editing that single-click may have started
    e.api.stopEditing();
    const nativeEvent = e.event as MouseEvent | undefined;
    const ingId = commentRowKey(rowData);
    setCommentPopover({
      x: nativeEvent?.clientX ?? 0,
      y: nativeEvent?.clientY ?? 0,
      fieldName: colId,
      rowId: ingId,
    });
    setNewCommentText("");
  }, []);

  const submitComment = useCallback(async () => {
    if (!commentPopover || !newCommentText.trim() || !formulaCode) return;
    setSubmittingComment(true);
    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email ?? "anonymous";
    const { data, error } = await supabase.from("formula_comments").insert({
      formula_code: formulaCode,
      field_name: commentPopover.fieldName,
      row_id: commentPopover.rowId,
      comment_text: newCommentText.trim(),
      author_email: email,
    }).select().single();
    if (!error && data) {
      setComments((prev) => [...prev, data as CellComment]);
      setNewCommentText("");
    } else if (error) {
      console.error("Comment insert failed:", error.message);
      alert(t('formulaSheet.failedSaveComment') + error.message);
    }
    setSubmittingComment(false);
  }, [commentPopover, newCommentText, formulaCode, supabase]);

  const deleteComment = useCallback(async (commentId: number) => {
    const { error } = await supabase.from("formula_comments").delete().eq("id", commentId);
    if (!error) {
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } else {
      alert(t('formulaSheet.failedDeleteComment') + error.message);
    }
  }, [supabase]);

  // Close popover on outside click
  useEffect(() => {
    if (!commentPopover) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setCommentPopover(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [commentPopover]);

  /* ── Delete row ── */
  const deleteRow = useCallback((rowId: string) => {
    setRawRows((prev) => prev.filter((r) => r._rowId !== rowId));
  }, []);

  /* ── Split rows by section ── */
  const doughRows = useMemo(() => rows.filter((r) => r.component === "DOUGH"), [rows]);
  const fillingRows = useMemo(() => rows.filter((r) => r.component === "FILLING"), [rows]);

  /* ── Section totals ── */
  const totals = useMemo(() => {
    let grandGrams = 0, grandCost = 0;
    const sec: Record<string, { grams: number; cost: number }> = {};
    for (const r of rows) {
      const g = r.grams ?? 0;
      const c = r.cost_contribution ?? 0;
      grandGrams += g;
      grandCost += c;
      if (!sec[r.component]) sec[r.component] = { grams: 0, cost: 0 };
      sec[r.component].grams += g;
      sec[r.component].cost += c;
    }
    return { sec, grandGrams, grandCost };
  }, [rows]);

  const sectionFauxPctSum = useMemo(() => {
    const m: Record<string, number> = { DOUGH: 0, FILLING: 0 };
    for (const r of rawRows) {
      m[r.component] = (m[r.component] ?? 0) + (r._fauxPct ?? 0);
    }
    return m;
  }, [rawRows]);

  const makePinnedRow = useCallback((comp: string, label: string, rowType: string): FormulaRow[] => {
    const sg = totals.sec[comp]?.grams ?? 0;
    const sc = totals.sec[comp]?.cost ?? 0;
    const sectionRatio = comp === "DOUGH" ? doughRatio / 100 : (100 - doughRatio) / 100;
    return [{
      _rowId: "pin_" + comp, formula_code: "", component: label,
      ingredient_id: null, pct_of_component: null, display_order: -1, row_type: rowType,
      grams: sg, pct_of_formula: null,
      pct_component_display: 1.0,
      pct_composite_display: sectionRatio,
      supplier: null, lot_code: null, moisture_pct: null,
      cost_per_kg: null, cost_contribution: sc,
      notes: null, process_step: null, process_instruction: null,
      common_name: "", _isPinned: true,
      _fauxPct: sectionFauxPctSum[comp] ?? 0,
    }];
  }, [totals, doughRatio, sectionFauxPctSum]);

  const doughPinned = useMemo(() => makePinnedRow("DOUGH", "DOUGH TOTAL", "subtotal"), [makePinnedRow]);
  const fillingPinned = useMemo(() => makePinnedRow("FILLING", "FILLING TOTAL", "subtotal"), [makePinnedRow]);

  /* ── Comment-aware cell renderer wrapper ── */
  const makeCommentCellRenderer = useCallback(
    (colKey: string, renderValue: (params: any) => React.ReactNode) => {
      return (params: any) => {
        const rowData = params.data as FormulaRow | undefined;
        if (!rowData || rowData._isPinned) return renderValue(params);
        const ingId = commentRowKey(rowData);
        const k = commentKey(colKey, ingId);
        const hasComments = commentsByKey.has(k);
        const cellComments = hasComments ? commentsByKey.get(k) : undefined;
        const latestComment = cellComments ? cellComments[cellComments.length - 1] : undefined;
        return React.createElement(
          "div",
          {
            style: { position: "relative", width: "100%", height: "100%" },
            title: latestComment
              ? authorAbbrev(latestComment.author_email) + ": " + latestComment.comment_text
              : undefined,
          },
          renderValue(params),
          hasComments
            ? React.createElement("div", {
                style: {
                  position: "absolute", top: 0, right: 0, width: 0, height: 0,
                  borderLeft: "6px solid transparent",
                  borderTop: "6px solid #3b82f6",
                },
              })
            : null,
        );
      };
    },
    [commentsByKey],
  );

  /* ── All column definitions (with _colKey for visibility toggling) ── */
  const allColumns: (ColDef<FormulaRow> & { _colKey: string })[] = useMemo(
    () => [
      {
        _colKey: "_delete", headerName: "", width: 40, sortable: false, resizable: false,
        editable: false, suppressSizeToFit: true,
        cellRenderer: (p: any) => {
          if (p.data?._isPinned) return "";
          const id = p.data?._rowId;
          return React.createElement("span", {
            style: { cursor: "pointer", color: "#999", fontSize: "14px" },
            onClick: () => id && deleteRow(id),
            title: t('sheet.deleteRow'),
          }, "\u2715");
        },
      },
      {
        _colKey: "display_order", headerName: "#", field: "display_order", width: 45,
        editable: false,
        valueFormatter: (p: any) => (p.data?._isPinned ? "" : String(p.value ?? "")),
      },
      {
        _colKey: "component", headerName: t('grid.component'), field: "component", width: 80, editable: false,
        cellStyle: (p: any) => {
          if (p.data?.row_type === "grandtotal") return { fontWeight: "bold", background: "#d4d4d4" };
          if (p.data?.row_type === "subtotal") return { fontWeight: "bold", background: "#e8e8e8" };
          return null;
        },
      },
      {
        _colKey: "ingredient_id", headerName: t('grid.ingredient'), field: "ingredient_id", width: 180,
        editable: false,
        cellRenderer: makeCommentCellRenderer("ingredient_id", (p: any) => {
          if (p.data?._isPinned) return "";
          if (!p.value) {
            return React.createElement("span", {
              style: { color: "#aaa", fontStyle: "italic", cursor: "pointer" },
            }, t('sheet.clickToSelect'));
          }
          const ing = ingMap.get(p.value);
          return React.createElement("span", {
            style: { cursor: "pointer" },
          }, ing ? ing.common_name : p.value);
        }),
      },
      {
        _colKey: "common_name", headerName: t('grid.commonName'), field: "common_name" as any, width: 160,
        editable: false,
        cellRenderer: makeCommentCellRenderer("common_name", (p: any) => p.value ?? ""),
      },
      {
        _colKey: "pct_component_display",
        // Header text differs at the first word (see grid.pctComponent); width 130 so it
        // does not truncate at all. Grid, CSV export and Korean all read from i18n.
        headerName: t('grid.pctComponent'),
        field: "pct_component_display" as any, width: 130, editable: false,
        valueFormatter: (p: any) => (p.value != null ? (p.value * 100).toFixed(2) + "%" : ""),
        cellStyle: (p: any) => {
          if (p.data?.row_type === "subtotal") return { fontWeight: "bold", background: "#e8e8e8" };
          return null;
        },
      },
      {
        _colKey: "pct_composite_display",
        headerName: t('grid.pctComposite'),
        field: "pct_composite_display" as any, width: 130, editable: false,
        valueFormatter: (p: any) => (p.value != null ? (p.value * 100).toFixed(2) + "%" : ""),
        cellStyle: (p: any) => {
          if (p.data?.row_type === "grandtotal") return { fontWeight: "bold", background: "#d4d4d4" };
          if (p.data?.row_type === "subtotal") return { fontWeight: "bold", background: "#e8e8e8" };
          return null;
        },
      },
      {
        _colKey: "grams", headerName: t('grid.gramsBatch'), field: "grams", width: 90,
        editable: (p: any) => !p.data?._isPinned,
        valueSetter: (p: any) => {
          const raw = String(p.newValue).trim();
          let n = parseFloat(raw);
          if (isNaN(n)) { const ev = tryEvalExpr(raw); if (ev == null) return false; n = ev; }
          p.data.grams = n;
          return true;
        },
        cellStyle: (p: any) => {
          if (p.data?.row_type === "grandtotal") return { fontWeight: "bold", background: "#d4d4d4" };
          if (p.data?.row_type === "subtotal") return { fontWeight: "bold", background: "#e8e8e8" };
          return null;
        },
        cellRenderer: makeCommentCellRenderer("grams", (p: any) => p.value != null ? Number(p.value).toFixed(3) : ""),
      },
      {
        _colKey: "supplier", headerName: t('grid.supplier'), field: "supplier", width: 130,
        editable: (p: any) => !p.data?._isPinned,
      },
      {
        _colKey: "cost_per_kg", headerName: t('grid.costPerKg'), field: "cost_per_kg", width: 85,
        editable: false,
        valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(2) : ""),
      },
      {
        _colKey: "cost_contribution", headerName: t('grid.costContrib'), field: "cost_contribution", width: 95,
        editable: false,
        valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(4) : ""),
        cellStyle: (p: any) => {
          if (p.data?.row_type === "grandtotal") return { fontWeight: "bold", background: "#d4d4d4" };
          if (p.data?.row_type === "subtotal") return { fontWeight: "bold", background: "#e8e8e8" };
          return null;
        },
      },
      {
        _colKey: "moisture_pct", headerName: t('grid.moisture'), field: "moisture_pct", width: 75,
        editable: false,
        valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(1) + "%" : ""),
      },
      {
        _colKey: "notes", headerName: t('grid.notes'), field: "notes", width: 140,
        editable: (p: any) => !p.data?._isPinned,
        cellRenderer: makeCommentCellRenderer("notes", (p: any) => p.value ?? ""),
      },
      {
        _colKey: "process_step", headerName: t('grid.processStep'), field: "process_step", width: 90,
        editable: (p: any) => !p.data?._isPinned,
      },
      {
        _colKey: "process_instruction", headerName: t('grid.processInstructions'), field: "process_instruction", width: 200,
        editable: (p: any) => !p.data?._isPinned,
      },
      // Nutritional columns — all read-only, looked up from ingredient
      ...NUTRI_COLS.map(({ key, label }) => ({
        _colKey: "nutri_" + key, headerName: label, width: 90, editable: false as const,
        valueGetter: (p: any) => {
          if (p.data?._isPinned || !p.data?.ingredient_id) return null;
          const ing = ingMap.get(p.data.ingredient_id);
          return ing ? (ing as any)[key] ?? null : null;
        },
        valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(2) : ""),
      })),
    ],
    [deleteRow, ingMap, makeCommentCellRenderer, t, i18n.language],
  );

  const columnDefs = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c._colKey)),
    [allColumns, hiddenCols],
  );

  const toggleableCols = useMemo(() => allColumns.filter((c) => c._colKey !== "_delete"), [allColumns]);

  /* ── Per-section column defs (apply batch-mode transform) ── */
  const buildSectionColumns = useCallback(
    (section: "DOUGH" | "FILLING", batchOn: boolean, inputMode: "g" | "pct", batchSize: number) => {
      if (!batchOn || inputMode === "g") return columnDefs;
      const out: ColDef<FormulaRow>[] = [];
      for (const c of columnDefs) {
        if ((c as any)._colKey === "grams") {
          // Rename "Grams (batch)" → "%" and edit faux pct
          out.push({
            ...c,
            headerName: "%",
            field: undefined,
            valueGetter: (p: any) => {
              if (p.data?._isPinned) return p.data._fauxPct ?? 0;
              return p.data?._fauxPct ?? 0;
            },
            valueSetter: (p: any) => {
              if (p.data?._isPinned) return false;
              const raw = String(p.newValue).trim();
              let n = parseFloat(raw);
              if (isNaN(n)) { const ev = tryEvalExpr(raw); if (ev == null) return false; n = ev; }
              const grams = (n * batchSize) / 100;
              p.data._fauxPct = n;
              p.data.grams = grams;
              return true;
            },
            cellStyle: (p: any) => {
              if (p.data?._isPinned) {
                const sum = p.data._fauxPct ?? 0;
                const isHundred = Math.abs(sum - 100) < 0.005;
                return {
                  fontWeight: "bold",
                  background: "#e8e8e8",
                  color: isHundred ? "#16a34a" : "#dc2626",
                };
              }
              return null;
            },
            cellRenderer: makeCommentCellRenderer("grams", (p: any) => {
              const v = p.value;
              if (v == null) return "";
              return Number(v).toFixed(2) + "%";
            }),
          } as ColDef<FormulaRow>);
          // Insert read-only "Batch grams" column directly after
          out.push({
            headerName: "Batch grams",
            field: "grams" as any,
            width: 95,
            editable: false,
            sortable: true,
            resizable: true,
            valueFormatter: (p: any) => (p.value != null ? Number(p.value).toFixed(3) : ""),
            cellStyle: (p: any) => {
              if (p.data?._isPinned) return { fontWeight: "bold", background: "#e8e8e8" };
              return { color: "#666" };
            },
          } as any);
        } else {
          out.push(c);
        }
      }
      return out;
    },
    [columnDefs, makeCommentCellRenderer],
  );

  const doughColumnDefs = useMemo(
    () => buildSectionColumns("DOUGH", doughBatchOn, doughInputMode, doughBatchSize),
    [buildSectionColumns, doughBatchOn, doughInputMode, doughBatchSize],
  );
  const fillingColumnDefs = useMemo(
    () => buildSectionColumns("FILLING", fillingBatchOn, fillingInputMode, fillingBatchSize),
    [buildSectionColumns, fillingBatchOn, fillingInputMode, fillingBatchSize],
  );

  /* ── Cell change handler (grams, notes, etc. — NOT ingredient_id) ── */
  const onCellValueChanged = useCallback(
    (e: CellValueChangedEvent<FormulaRow>) => {
      if (!e.data || e.data._isPinned) return;
      const updated = { ...e.data };
      setRawRows((prev) => prev.map((r) => r._rowId === updated._rowId ? updated : r));
    },
    [],
  );

  /* ── Ingredient picker handlers ── */
  const handleIngredientCellClick = useCallback((e: CellClickedEvent<FormulaRow>) => {
    const colId = e.column?.getColId?.() ?? "";
    if (colId !== "ingredient_id") return;
    const rowData = e.data as FormulaRow | undefined;
    if (!rowData || rowData._isPinned) return;
    const cellEl = (e.event?.target as HTMLElement)?.closest?.(".ag-cell") as HTMLElement | null;
    if (!cellEl) return;
    const rect = cellEl.getBoundingClientRect();
    setPickerState({
      rowId: rowData._rowId,
      position: { top: rect.bottom + 1, left: rect.left },
    });
  }, []);

  const handleIngredientSelect = useCallback(
    (ingredientId: string) => {
      if (!pickerState) return;
      const ing = ingMap.get(ingredientId);
      setRawRows((prev) =>
        prev.map((r) => {
          if (r._rowId !== pickerState.rowId) return r;
          return {
            ...r,
            ingredient_id: ingredientId,
            common_name: ing?.common_name ?? "",
            cost_per_kg: ing?.cost_per_kg ?? null,
            moisture_pct: ing?.moisture ?? null,
            supplier: ing?.supplier ?? null,
          };
        }),
      );
      setPickerState(null);
    },
    [pickerState, ingMap],
  );

  /* ── Add row ── */
  const addRow = useCallback(
    (component: "DOUGH" | "FILLING") => {
      const sectionRows = rawRows.filter((r) => r.component === component);
      const maxOrder = sectionRows.length > 0 ? Math.max(...sectionRows.map((r) => r.display_order)) : 0;
      const newRow: FormulaRow = {
        _rowId: rid(), formula_code: header.formula_code, component,
        ingredient_id: null, pct_of_component: null,
        display_order: maxOrder + 1, row_type: "ingredient",
        grams: 0, pct_of_formula: null,
        pct_component_display: null, pct_composite_display: null,
        supplier: null, lot_code: null, moisture_pct: null,
        cost_per_kg: null, cost_contribution: null, notes: null,
        process_step: null, process_instruction: null, common_name: "",
      };
      setRawRows((prev) => [...prev, newRow]);
    },
    [rawRows, header.formula_code],
  );

  /* ── Batch mode handlers ── */
  const toggleBatchMode = useCallback((section: "DOUGH" | "FILLING") => {
    if (section === "DOUGH") {
      if (!doughBatchOn) {
        const sum = totals.sec.DOUGH?.grams ?? 0;
        const initial = sum > 0 ? Math.round(sum * 1000) / 1000 : 500;
        setDoughBatchSize(initial);
        setDoughInputMode("g");
        setRawRows((prev) => prev.map((r) => r.component === "DOUGH" ? { ...r, _fauxPct: null } : r));
      } else {
        setRawRows((prev) => prev.map((r) => r.component === "DOUGH" ? { ...r, _fauxPct: null } : r));
      }
      setDoughBatchOn((on) => !on);
    } else {
      if (!fillingBatchOn) {
        const sum = totals.sec.FILLING?.grams ?? 0;
        const initial = sum > 0 ? Math.round(sum * 1000) / 1000 : 500;
        setFillingBatchSize(initial);
        setFillingInputMode("g");
        setRawRows((prev) => prev.map((r) => r.component === "FILLING" ? { ...r, _fauxPct: null } : r));
      } else {
        setRawRows((prev) => prev.map((r) => r.component === "FILLING" ? { ...r, _fauxPct: null } : r));
      }
      setFillingBatchOn((on) => !on);
    }
  }, [doughBatchOn, fillingBatchOn, totals]);

  const changeBatchSize = useCallback((section: "DOUGH" | "FILLING", newSize: number) => {
    if (!isFinite(newSize) || newSize < 0) return;
    if (section === "DOUGH") {
      const oldSize = doughBatchSize;
      setDoughBatchSize(newSize);
      if (doughInputMode === "g" && oldSize > 0 && newSize > 0) {
        const ratio = newSize / oldSize;
        setRawRows((prev) => prev.map((r) =>
          r.component === "DOUGH" ? { ...r, grams: (r.grams ?? 0) * ratio } : r,
        ));
      } else if (doughInputMode === "pct") {
        setRawRows((prev) => prev.map((r) => {
          if (r.component !== "DOUGH") return r;
          const fp = r._fauxPct ?? 0;
          return { ...r, grams: fp * newSize / 100 };
        }));
      }
    } else {
      const oldSize = fillingBatchSize;
      setFillingBatchSize(newSize);
      if (fillingInputMode === "g" && oldSize > 0 && newSize > 0) {
        const ratio = newSize / oldSize;
        setRawRows((prev) => prev.map((r) =>
          r.component === "FILLING" ? { ...r, grams: (r.grams ?? 0) * ratio } : r,
        ));
      } else if (fillingInputMode === "pct") {
        setRawRows((prev) => prev.map((r) => {
          if (r.component !== "FILLING") return r;
          const fp = r._fauxPct ?? 0;
          return { ...r, grams: fp * newSize / 100 };
        }));
      }
    }
  }, [doughBatchSize, fillingBatchSize, doughInputMode, fillingInputMode]);

  // Batch size commits once on blur/Enter, never per keystroke. A controlled
  // number input here eats the decimal point as you type ("500.5" becomes 5005)
  // and re-scales all grams on every keystroke, compounding through empty/zero
  // intermediate values.
  const commitBatchSize = useCallback((section: "DOUGH" | "FILLING", el: HTMLInputElement) => {
    const n = parseFloat(el.value);
    const current = section === "DOUGH" ? doughBatchSize : fillingBatchSize;
    if (!isFinite(n) || n <= 0) { el.value = String(current); return; }
    changeBatchSize(section, n);
  }, [doughBatchSize, fillingBatchSize, changeBatchSize]);

  const changeInputMode = useCallback((section: "DOUGH" | "FILLING", mode: "g" | "pct") => {
    if (section === "DOUGH") {
      if (mode === "pct" && doughInputMode === "g") {
        const bs = doughBatchSize;
        setRawRows((prev) => prev.map((r) => {
          if (r.component !== "DOUGH") return r;
          const fp = bs > 0 ? ((r.grams ?? 0) / bs) * 100 : 0;
          return { ...r, _fauxPct: Math.round(fp * 1000) / 1000 };
        }));
      } else if (mode === "g" && doughInputMode === "pct") {
        setRawRows((prev) => prev.map((r) => r.component === "DOUGH" ? { ...r, _fauxPct: null } : r));
      }
      setDoughInputMode(mode);
    } else {
      if (mode === "pct" && fillingInputMode === "g") {
        const bs = fillingBatchSize;
        setRawRows((prev) => prev.map((r) => {
          if (r.component !== "FILLING") return r;
          const fp = bs > 0 ? ((r.grams ?? 0) / bs) * 100 : 0;
          return { ...r, _fauxPct: Math.round(fp * 1000) / 1000 };
        }));
      } else if (mode === "g" && fillingInputMode === "pct") {
        setRawRows((prev) => prev.map((r) => r.component === "FILLING" ? { ...r, _fauxPct: null } : r));
      }
      setFillingInputMode(mode);
    }
  }, [doughInputMode, fillingInputMode, doughBatchSize, fillingBatchSize]);

  /* ── Import component from existing formula ── */
  const openImportModal = useCallback(async (targetComponent: "DOUGH" | "FILLING") => {
    setImportModal({ targetComponent });
    setImportSearch("");
    setImportSelectedCode(null);
    setImportSourceComponent(targetComponent);
    setImportPreviewRows([]);    const { data } = await supabase.from("formulas").select("formula_code, display_name").eq("is_active", true).order("formula_code");
    setImportFormulas(data ?? []);
  }, [supabase]);

  const loadImportPreview = useCallback(async (sourceCode: string, sourceComponent: "DOUGH" | "FILLING") => {
    const seq = ++importPreviewSeq.current;
    setImportLoading(true);
    const { data } = await supabase
      .from("formula_components")
      .select("*")
      .eq("formula_code", sourceCode)
      .eq("component", sourceComponent)
      .or("row_type.is.null,row_type.neq.process")
      .order("display_order");
    if (seq !== importPreviewSeq.current) return; // superseded by a newer selection
    const preview: FormulaRow[] = (data ?? []).map((r: any) => {
      const ing = r.ingredient_id ? ingMap.get(r.ingredient_id) : undefined;
      return {
        _rowId: rid(),
        formula_code: header.formula_code,
        component: importModal?.targetComponent ?? sourceComponent,
        ingredient_id: r.ingredient_id,
        pct_of_component: r.pct_of_component,
        display_order: r.display_order,
        row_type: "ingredient",
        grams: r.grams,
        pct_of_formula: null,
        pct_component_display: null,
        pct_composite_display: null,
        supplier: r.supplier ?? ing?.supplier ?? null,
        lot_code: r.lot_code,
        moisture_pct: ing?.moisture ?? null,
        cost_per_kg: ing?.cost_per_kg ?? null,
        cost_contribution: null,
        notes: r.notes,
        process_step: null,
        process_instruction: null,
        common_name: ing?.common_name ?? "",
      };
    });
    setImportPreviewRows(preview);
    setImportLoading(false);
  }, [supabase, ingMap, header.formula_code, importModal]);

  const confirmImport = useCallback(() => {
    if (!importModal || importPreviewRows.length === 0) return;
    const target = importModal.targetComponent;
    setRawRows((prev) => {
      const otherRows = prev.filter((r) => r.component !== target);
      const maxOrder = otherRows.length > 0 ? Math.max(...otherRows.map((r) => r.display_order)) : 0;
      const imported = importPreviewRows.map((r, idx) => ({
        ...r,
        _rowId: rid(),
        component: target,
        display_order: maxOrder + idx + 1,
      }));
      return [...otherRows, ...imported];
    });
    setImportModal(null);
  }, [importModal, importPreviewRows]);

  /* ── CSV Export ── */
  const downloadCsv = useCallback(() => {
    const csvField = (v: any): string => {
      if (v == null) return "";
      const s = String(v);
      if (s.includes(",") || s.includes('"') || s.includes("\n")) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };

    const doughRows = rows.filter((r) => r.component === "DOUGH");
    const fillingRows = rows.filter((r) => r.component === "FILLING");

    // Determine visible nutritional columns
    const visibleNutriCols = NUTRI_COLS.filter((c) => !hiddenCols.has("nutri_" + c.key));

    const baseHeaders = [
      "Component", "Ingredient ID", "Common Name", "Grams",
      "Component % w/w", "Composite % w/w", "Supplier",
      "Cost EUR/kg", "Cost Contribution", "Moisture %",
      "Notes", "Process Step", "Process Instructions",
    ];
    const nutriHeaders = visibleNutriCols.map((c) => c.label);
    const allHeaders = [...baseHeaders, ...nutriHeaders];

    const rowToCsv = (r: FormulaRow): string => {
      const ing = r.ingredient_id ? ingMap.get(r.ingredient_id) : undefined;
      const base = [
        r.component,
        r.ingredient_id ?? "",
        r.common_name ?? "",
        r.grams != null ? r.grams.toFixed(2) : "",
        r.pct_component_display != null ? (r.pct_component_display * 100).toFixed(4) : "",
        r.pct_composite_display != null ? (r.pct_composite_display * 100).toFixed(4) : "",
        r.supplier ?? "",
        r.cost_per_kg != null ? r.cost_per_kg.toFixed(2) : "",
        r.cost_contribution != null ? r.cost_contribution.toFixed(4) : "",
        r.moisture_pct != null ? String(r.moisture_pct) : "",
        r.notes ?? "",
        r.process_step ?? "",
        r.process_instruction ?? "",
      ];
      const nutri = visibleNutriCols.map((c) => {
        const val = ing ? (ing as any)[c.key] : null;
        return val != null ? String(val) : "";
      });
      return [...base, ...nutri].map(csvField).join(",");
    };

    const totalRow = (label: string, sectionRows: FormulaRow[]): string => {
      const totalG = sectionRows.reduce((s, r) => s + (r.grams ?? 0), 0);
      const totalCost = sectionRows.reduce((s, r) => s + (r.cost_contribution ?? 0), 0);
      const vals = [label, "", "", totalG.toFixed(2), "100.0000", "", "", "", totalCost.toFixed(4), "", "", "", ""];
      const blanks = visibleNutriCols.map(() => "");
      return [...vals, ...blanks].map(csvField).join(",");
    };

    const lines: string[] = [];
    lines.push(`Formula Code,${csvField(header.formula_code)}`);
    lines.push(`Display Name,${csvField(header.display_name ?? "")}`);
    lines.push(`Dough Ratio,${doughRatio}%`);
    lines.push(`Serving Size,${header.serving_size_g ?? 100}g`);
    lines.push("");
    lines.push(allHeaders.map(csvField).join(","));

    doughRows.forEach((r) => lines.push(rowToCsv(r)));
    fillingRows.forEach((r) => lines.push(rowToCsv(r)));

    lines.push(totalRow("DOUGH TOTAL", doughRows));
    lines.push(totalRow("FILLING TOTAL", fillingRows));
    lines.push(totalRow("GRAND TOTAL", [...doughRows, ...fillingRows]));

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${header.formula_code}_formula_export.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [rows, ingMap, header, doughRatio, hiddenCols]);
  /* ── Save (overwrite existing) ── */
  const handleSave = useCallback(async () => {
    if (isNew || !formulaCode) return;
    if (!window.confirm(t('formulaSheet.confirmOverwrite', { code: formulaCode }))) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const sectionGrams: Record<string, number> = {};
      for (const r of rawRows) {
        sectionGrams[r.component] = (sectionGrams[r.component] ?? 0) + (r.grams ?? 0);
      }
      const combinedNotes = combineNotes(formulaNotes, processNotes, observationNotes);

      // Update formula header. Like the DELETE below, an RLS-blocked UPDATE
      // returns 200 with zero rows and no error, so verify a row came back.
      const { data: hRows, error: hErr } = await supabase.from("formulas").update({
        display_name: header.display_name || "Untitled",
        piece_weight_g: header.piece_weight_g ?? 0,
        serving_size_g: header.serving_size_g ?? 0,
        dough_ratio_pct: header.dough_ratio_pct ?? 100,
        owner_name: header.owner_name,
        notes: combinedNotes,
        ratio_mode: header.ratio_mode ?? "as_mixed",
        final_moisture_mode: header.final_moisture_mode ?? null,
        final_moisture_whole: header.final_moisture_whole ?? null,
        final_moisture_dough: header.final_moisture_dough ?? null,
        final_moisture_filling: header.final_moisture_filling ?? null,
      }).eq("formula_code", formulaCode).select();
      if (hErr) throw new Error(hErr.message);
      if (!hRows || hRows.length === 0) {
        throw new Error(t('formulaSheet.updateBlocked'));
      }

      // Process rows are loaded read-only (filtered out of the editor above),
      // so the delete + re-insert below would permanently wipe them. Fetch and
      // restore them around the rewrite.
      const { data: procRows, error: pErr } = await supabase
        .from("formula_components")
        .select("*")
        .eq("formula_code", formulaCode)
        .eq("row_type", "process");
      if (pErr) throw new Error(pErr.message);

      // Delete old components. RLS blocks DELETE silently (no error, zero rows
      // removed — project rule #9), and the re-insert below then collides with
      // the surviving rows on uq_formula_display_order. Verify the delete
      // actually happened and fail loudly instead.
      const { error: dErr } = await supabase.from("formula_components").delete().eq("formula_code", formulaCode);
      if (dErr) throw new Error(dErr.message);
      const { count: leftCount, error: cErr } = await supabase
        .from("formula_components")
        .select("*", { count: "exact", head: true })
        .eq("formula_code", formulaCode);
      if (cErr) throw new Error(cErr.message);
      if ((leftCount ?? 0) > 0) {
        throw new Error(t('formulaSheet.deleteBlocked'));
      }

      // Insert new components with sequential display_order, dough first
      const ordered = [...rawRows.filter((r) => r.component === "DOUGH"), ...rawRows.filter((r) => r.component !== "DOUGH")];
      const inserts = ordered.map((r, idx) => {
        const sg = sectionGrams[r.component] ?? 0;
        const g = r.grams ?? 0;
        const pctComp = sg > 0 ? g / sg : null;
        const sectionRatio = r.component === "DOUGH" ? (header.dough_ratio_pct ?? 100) / 100 : (100 - (header.dough_ratio_pct ?? 100)) / 100;
        return {
          formula_code: formulaCode,
          component: r.component,
          ingredient_id: r.ingredient_id || null,
          display_order: idx + 1,
          row_type: r.row_type,
          pct_of_component: pctComp,
          grams: r.grams,
          pct_of_formula: pctComp != null ? pctComp * sectionRatio : null,
          supplier: r.supplier,
          lot_code: r.lot_code,
          notes: r.notes,
          process_step: r.process_step,
          process_instruction: r.process_instruction,
        };
      });
      if (inserts.length > 0) {
        const { error: iErr } = await supabase.from("formula_components").insert(inserts);
        if (iErr) throw new Error(iErr.message);
      }
      if (procRows && procRows.length > 0) {
        const { error: prErr } = await supabase.from("formula_components").insert(
          procRows.map((r: any) => {
            const { id, ...rest } = r;
            return rest;
          }),
        );
        if (prErr) throw new Error(prErr.message);
      }
      // BUGS.md #24: declared dough:filling ratio contradicting the entered
      // grams saves silently — 23/44 live formulas already disagree. Warn on
      // the success banner (never block: legacy data must stay saveable).
      let savedText = t('formulaSheet.saved', { code: formulaCode });
      if (header.dough_ratio_pct != null) {
        const gramsOf = (section: string) =>
          rawRows
            .filter((r: any) => r.component === section && r.grams != null && r.grams > 0)
            .reduce((s: number, r: any) => s + r.grams, 0);
        const doughG = gramsOf("DOUGH");
        const fillingG = gramsOf("FILLING");
        if (doughG > 0 && fillingG > 0) {
          const implied = (doughG / (doughG + fillingG)) * 100;
          if (Math.abs(implied - header.dough_ratio_pct) > 2) {
            savedText += ` — ${t('formulaSheet.ratioGramsMismatch', {
              declared: header.dough_ratio_pct,
              implied: implied.toFixed(1),
            })}`;
          }
        }
      }
      setSaveMsg({ type: "ok", text: savedText });
      logActivity("formula_save", { formula_code: formulaCode, display_name: header.display_name, via: "sheet" });
    } catch (err: any) {
      setSaveMsg({ type: "err", text: err.message || t('formulaSheet.saveFailed') });
    } finally {
      setSaving(false);
    }
  }, [rawRows, header, supabase, isNew, formulaCode, formulaNotes, processNotes, observationNotes, t]);

  /* ── Save As ── */
  const handleSaveAs = useCallback(async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      // Compute section totals for pct_of_component
      const sectionGrams: Record<string, number> = {};
      let grandGrams = 0;
      for (const r of rawRows) {
        const g = r.grams ?? 0;
        sectionGrams[r.component] = (sectionGrams[r.component] ?? 0) + g;
        grandGrams += g;
      }

      // Order: dough rows first, then filling, sequential display_order
      const ordered = [...rawRows.filter((r) => r.component === "DOUGH"), ...rawRows.filter((r) => r.component !== "DOUGH")];
      const components = ordered.map((r, idx) => {
        const sg = sectionGrams[r.component] ?? 0;
        const g = r.grams ?? 0;
        const pctComp = sg > 0 ? g / sg : null;
        const dr = header.dough_ratio_pct ?? 100;
        const sectionRatio = r.component === "DOUGH" ? dr / 100 : (100 - dr) / 100;
        return {
          component: r.component,
          ingredient_id: r.ingredient_id || null,
          display_order: idx + 1,
          row_type: r.row_type,
          pct_of_component: pctComp,
          grams: r.grams,
          pct_of_formula: pctComp != null ? pctComp * sectionRatio : null,
          supplier: r.supplier,
          lot_code: r.lot_code,
          notes: r.notes,
          process_step: r.process_step,
          process_instruction: r.process_instruction,
        };
      });

      const combinedNotes = combineNotes(formulaNotes, processNotes, observationNotes);
      const { data, error } = await supabase.rpc("save_formula_as", {
        p_display_name: header.display_name || "Untitled",
        p_piece_weight_g: header.piece_weight_g ?? 0,
        p_serving_size_g: header.serving_size_g ?? 0,
        p_dough_ratio_pct: header.dough_ratio_pct ?? 100,
        p_owner_name: header.owner_name,
        p_notes: combinedNotes,
        p_source_formula_code: isNew ? null : header.formula_code,
        p_components: components,
      });
      if (error) throw new Error(error.message);
      // Update the new formula with ratio/moisture settings. Like handleSave,
      // verify the update actually landed (RLS blocks silently with 0 rows).
      const newCode = data as string;
      if (newCode) {
        const { data: uRows, error: uErr } = await supabase.from("formulas").update({
          ratio_mode: header.ratio_mode ?? "as_mixed",
          final_moisture_mode: header.final_moisture_mode ?? null,
          final_moisture_whole: header.final_moisture_whole ?? null,
          final_moisture_dough: header.final_moisture_dough ?? null,
          final_moisture_filling: header.final_moisture_filling ?? null,
        }).eq("formula_code", newCode).select();
        if (uErr) throw new Error(uErr.message);
        if (!uRows || uRows.length === 0) {
          throw new Error(t('formulaSheet.updateBlocked'));
        }
      }
      setSaveMsg({ type: "ok", text: t('formulaSheet.savedAs', { code: data }) });
      logActivity("formula_save_as", { from: formulaCode, to: String(data), display_name: header.display_name, via: "sheet" });
      if (newCode && onNavigateToFormula) {
        onNavigateToFormula(newCode);
      }
    } catch (err: any) {
      setSaveMsg({ type: "err", text: err.message || t('formulaSheet.saveFailed') });
    } finally {
      setSaving(false);
    }
  }, [rawRows, header, supabase, isNew, formulaNotes, processNotes, observationNotes, t, onNavigateToFormula]);

  /* ── Styles ── */
  const lbl: React.CSSProperties = { fontSize: "10px", fontWeight: 600, color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" };
  const inp: React.CSSProperties = { fontSize: "13px", padding: "3px 6px", border: "1px solid #d0d0d0", background: "#fff", outline: "none" };
  const roInp: React.CSSProperties = { ...inp, background: "#f5f5f5", color: "#666" };
  const btn: React.CSSProperties = { padding: "3px 10px", fontSize: "11px", cursor: "pointer", border: "1px solid #c8c8c8", background: "#fff" };
  // Batch-mode radio labels sit on a light lilac bar. Without an explicit colour they
  // inherit the near-white global body colour and read as white on white.
  const radioLbl: React.CSSProperties = {
    fontSize: "11px", color: "#6d28d9", fontWeight: 600,
    display: "flex", alignItems: "center", gap: "3px", cursor: "pointer",
  };

  if (loading) {
    return <div style={{ padding: "40px", textAlign: "center", color: "#666", fontSize: "13px" }}>{t('formulaSheet.loading', { code: formulaCode })}</div>;
  }

  if (notFound) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#666", fontSize: "13px" }}>
        <button onClick={onBack} style={{ padding: "4px 10px", fontSize: "12px", cursor: "pointer", border: "1px solid #d0d0d0", background: "#f0f0f0", marginBottom: "12px" }}>{t('common.back')}</button>
        <div>{t('formulaSheet.formulaNotFound')} {formulaCode}</div>
      </div>
    );
  }

  const toggleCol = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {/* ── Comment Popover ── */}
      {commentPopover && (
        <div
          ref={popoverRef}
          style={{
            position: "fixed", left: commentPopover.x, top: commentPopover.y,
            zIndex: 9999, background: "#fff", border: "1px solid #ccc",
            borderRadius: "4px", padding: "8px", width: "260px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)", maxHeight: "240px",
            display: "flex", flexDirection: "column",
          }}
        >
          <div style={{ fontSize: "10px", fontWeight: 700, color: "#666", textTransform: "uppercase", marginBottom: "4px" }}>
            Comments: {commentPopover.fieldName}
          </div>
          <div style={{ flex: 1, overflowY: "auto", maxHeight: "140px", marginBottom: "6px" }}>
            {(commentsByKey.get(commentKey(commentPopover.fieldName, commentPopover.rowId)) ?? []).map((c) => (
              <div key={c.id} style={{ fontSize: "11px", marginBottom: "4px", borderBottom: "1px solid #eee", paddingBottom: "3px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>
                    <span style={{ fontWeight: 700, color: "#3b82f6" }}>{authorAbbrev(c.author_email)}</span>
                    <span style={{ color: "#999", marginLeft: "4px" }}>
                      {new Date(c.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </span>
                  {currentUserEmail && c.author_email === currentUserEmail && (
                    <span
                      onClick={() => deleteComment(c.id)}
                      style={{ cursor: "pointer", color: "#b91c1c", fontSize: "10px", padding: "0 2px" }}
                      title={t('sheet.deleteComment')}
                    >{"\u2715"}</span>
                  )}
                </div>
                <div style={{ color: "#333" }}>{c.comment_text}</div>
              </div>
            ))}
            {!(commentsByKey.get(commentKey(commentPopover.fieldName, commentPopover.rowId)) ?? []).length && (
              <div style={{ color: "#999", fontSize: "11px" }}>{t('sheet.noCommentsYet')}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: "4px" }}>
            <input
              type="text" placeholder={t('sheet.addComment')}
              value={newCommentText} onChange={(e) => setNewCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitComment(); }}
              style={{ flex: 1, fontSize: "11px", padding: "3px 6px", border: "1px solid #ccc", outline: "none" }}
              autoFocus
            />
            <button
              onClick={submitComment} disabled={submittingComment || !newCommentText.trim()}
              style={{ fontSize: "11px", padding: "3px 8px", cursor: "pointer", border: "1px solid #3b82f6", background: "#3b82f6", color: "#fff" }}
            >{t('formulaSheet.add')}</button>
          </div>
        </div>
      )}

      <style>{`
        .ag-theme-alpine .ag-header-cell-label { font-size: 11px; }
        .formula-grid .ag-cell { border: 1px solid #333333 !important; }
        .formula-grid .ag-header-cell { border: 1px solid #333333 !important; }
        .formula-grid .ag-row { border: none !important; }
        .formula-grid .ag-pinned-bottom-row .ag-cell { border: 1px solid #333333 !important; }
      `}</style>

      {/* ── Top bar ── */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap", padding: "4px 0" }}>
        <button onClick={onBack} style={{ padding: "4px 10px", fontSize: "12px", cursor: "pointer", border: "1px solid #d0d0d0", background: "#f0f0f0" }}>{t('common.back')}</button>
        <div>
          <div style={lbl}>{t('formulaSheet.code')}</div>
          <input value={header.formula_code} readOnly style={{ ...roInp, width: "60px", fontFamily: "monospace" }} />
        </div>
        <div>
          <div style={lbl}>{t('formulaSheet.displayName')}</div>
          <input value={header.display_name ?? ""} onChange={(e) => setHeader((h) => ({ ...h, display_name: e.target.value }))} style={{ ...inp, width: "180px" }} />
        </div>
        <div>
          <div style={lbl}>{t('formulaSheet.servingG')}</div>
          <input lang="en" type="number" value={header.serving_size_g ?? ""} onChange={(e) => setHeader((h) => ({ ...h, serving_size_g: e.target.value ? Number(e.target.value) : null }))} style={{ ...inp, width: "70px" }} />
        </div>
        <div>
          <div style={lbl}>{t('formulaSheet.owner')}</div>
          <input value={header.owner_name ?? ""} onChange={(e) => setHeader((h) => ({ ...h, owner_name: e.target.value }))} style={{ ...inp, width: "100px" }} />
        </div>
        <div>
          <div style={lbl}>{t('formulaSheet.formulaNotes')}</div>
          <input value={formulaNotes} onChange={(e) => setFormulaNotes(e.target.value)} style={{ ...inp, width: "160px" }} />
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => addRow("DOUGH")} style={btn}>{t('sheet.addDoughIngredient')}</button>
        <button onClick={() => addRow("FILLING")} style={btn}>{t('sheet.addFillingIngredient')}</button>
        <span style={{ fontSize: "10px", color: "#999" }}>|</span>
        <button onClick={() => openImportModal("DOUGH")} style={{ ...btn, color: "#7c3aed" }}>{t('sheet.importDough')}</button>
        <button onClick={() => openImportModal("FILLING")} style={{ ...btn, color: "#7c3aed" }}>{t('sheet.importFilling')}</button>
        <span style={{ fontSize: "10px", color: "#999" }}>|</span>
        <div style={{ position: "relative" }}>
          <button onClick={() => setColMenuOpen((o) => !o)} style={btn}>{t('formulaSheet.columns')}</button>
          {colMenuOpen && (
            <div style={{
              position: "absolute", top: "100%", left: 0, zIndex: 100,
              background: "#fff", border: "1px solid #ccc", padding: "6px 8px",
              maxHeight: "300px", overflowY: "auto", width: "200px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}>
              {toggleableCols.map((c) => (
                <label key={c._colKey} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", padding: "2px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={!hiddenCols.has(c._colKey)} onChange={() => toggleCol(c._colKey)} />
                  {c.headerName}
                </label>
              ))}
            </div>
          )}
        </div>
        <span style={{ fontSize: "11px", color: "#999" }}>{t('formulaSheet.rows')}: {rows.length}</span>
        <span style={{ fontSize: "10px", color: "#999" }}>|</span>
        <button onClick={downloadCsv} style={{ ...btn, display: "flex", alignItems: "center", gap: "4px" }} title={t('sheet.downloadCSV')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {t('formulaSheet.csv')}
        </button>
        <span style={{ fontSize: "10px", color: "#999" }}>|</span>
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setPrintMenuOpen((o) => !o)}
            style={{ ...btn, display: "flex", alignItems: "center", gap: "4px" }}
            title={t('formulaSheet.generateReport')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            {t('formulaSheet.report')}
          </button>
          {printMenuOpen && (
            <div style={{
              position: "absolute", top: "100%", left: 0, zIndex: 100,
              background: "#fff", border: "1px solid #ccc", borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)", width: "120px",
            }}>
              <div
                onClick={() => { setPrintMenuOpen(false); setReportExportType("pdf"); }}
                style={{ padding: "6px 10px", fontSize: "11px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f3ff")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                &#128196; {t('formulaSheet.exportPdf')}
              </div>
              <div
                onClick={() => { setPrintMenuOpen(false); setReportExportType("ppt"); }}
                style={{ padding: "6px 10px", fontSize: "11px", cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f3ff")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                &#128202; {t('formulaSheet.exportPpt')}
              </div>
            </div>
          )}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center" }}>
          {saveMsg && <span style={{ fontSize: "12px", color: saveMsg.type === "ok" ? "#16a34a" : "#dc2626", fontWeight: 500 }}>{saveMsg.text}</span>}
          {!isNew && (
            <button
              onClick={handleSave} disabled={saving}
              style={{ padding: "5px 14px", fontSize: "12px", fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", border: "1px solid #16a34a", background: "#16a34a", color: "#fff", opacity: saving ? 0.6 : 1 }}
            >
              {saving ? t('formulaSheet.saving') : t('formulaSheet.save')}
            </button>
          )}
          <button
            onClick={handleSaveAs} disabled={saving}
            style={{ padding: "5px 14px", fontSize: "12px", fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", border: "1px solid #7c3aed", background: "#7c3aed", color: "#fff", opacity: saving ? 0.6 : 1 }}
          >
            {saving ? t('formulaSheet.saving') : t('formulaSheet.saveAsNewFormula')}
          </button>
        </div>
      </div>

      {/* ── DOUGH Grid ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "6px 0 2px", position: "relative" }}>
        <div style={{ fontWeight: 700, fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px", color: "#333" }}>{t('formulation.dough')}</div>
        <button
          onClick={() => toggleBatchMode("DOUGH")}
          style={{
            ...btn,
            background: doughBatchOn ? "#7c3aed" : "#fff",
            color: doughBatchOn ? "#fff" : "#7c3aed",
            border: "1px solid #7c3aed",
          }}
        >
          Batch mode {doughBatchOn ? "✓" : ""}
        </button>
        {doughBatchOn && (
          <div style={{
            display: "flex", alignItems: "center", gap: "10px",
            background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: "4px",
            padding: "4px 10px",
          }}>
            <label style={{ fontSize: "11px", color: "#6d28d9", fontWeight: 600 }}>
              Batch size (g):
              <input
                lang="en"
                type="text"
                inputMode="decimal"
                key={`dough-bs-${doughBatchSize}`}
                defaultValue={String(doughBatchSize)}
                onBlur={(e) => commitBatchSize("DOUGH", e.currentTarget)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                style={{ ...inp, width: "80px", marginLeft: "6px" }}
              />
            </label>
            <span style={{ fontSize: "11px", color: "#6d28d9", fontWeight: 600 }}>
              {t('formulaSheet.enteringIn')}
            </span>
            <label htmlFor="doughBatchInputModeG" style={radioLbl}>
              <input
                id="doughBatchInputModeG"
                type="radio"
                name="doughBatchInputMode"
                checked={doughInputMode === "g"}
                onChange={() => changeInputMode("DOUGH", "g")}
              />
              g
            </label>
            <label htmlFor="doughBatchInputModePct" style={radioLbl}>
              <input
                id="doughBatchInputModePct"
                type="radio"
                name="doughBatchInputMode"
                checked={doughInputMode === "pct"}
                onChange={() => changeInputMode("DOUGH", "pct")}
              />
              %
            </label>
            <button
              onClick={() => toggleBatchMode("DOUGH")}
              style={{ ...btn, padding: "2px 6px", fontSize: "10px", color: "#6d28d9" }}
              title="Close batch mode"
            >
              {"✕"}
            </button>
          </div>
        )}
      </div>
      <div className="ag-theme-alpine formula-grid" style={{ width: "100%" }}>
        <AgGridReact<FormulaRow>
          rowData={doughRows}
          columnDefs={doughColumnDefs}
          defaultColDef={{ sortable: true, resizable: true }}
          pinnedBottomRowData={doughPinned}
          onCellValueChanged={onCellValueChanged}
          onCellClicked={handleIngredientCellClick}
          onCellDoubleClicked={handleCellDoubleClicked}
          getRowId={(p) => p.data._rowId}
          domLayout="autoHeight"
          singleClickEdit
          stopEditingWhenCellsLoseFocus={false}
          rowHeight={28}
          headerHeight={32}
          suppressMovableColumns
          animateRows={false}
        />
      </div>

      {/* ── FILLING Grid ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "6px 0 2px", position: "relative" }}>
        <div style={{ fontWeight: 700, fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px", color: "#333" }}>{t('formulation.filling')}</div>
        <button
          onClick={() => toggleBatchMode("FILLING")}
          style={{
            ...btn,
            background: fillingBatchOn ? "#7c3aed" : "#fff",
            color: fillingBatchOn ? "#fff" : "#7c3aed",
            border: "1px solid #7c3aed",
          }}
        >
          Batch mode {fillingBatchOn ? "✓" : ""}
        </button>
        {fillingBatchOn && (
          <div style={{
            display: "flex", alignItems: "center", gap: "10px",
            background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: "4px",
            padding: "4px 10px",
          }}>
            <label style={{ fontSize: "11px", color: "#6d28d9", fontWeight: 600 }}>
              Batch size (g):
              <input
                lang="en"
                type="text"
                inputMode="decimal"
                key={`filling-bs-${fillingBatchSize}`}
                defaultValue={String(fillingBatchSize)}
                onBlur={(e) => commitBatchSize("FILLING", e.currentTarget)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                style={{ ...inp, width: "80px", marginLeft: "6px" }}
              />
            </label>
            <span style={{ fontSize: "11px", color: "#6d28d9", fontWeight: 600 }}>
              {t('formulaSheet.enteringIn')}
            </span>
            <label htmlFor="fillingBatchInputModeG" style={radioLbl}>
              <input
                id="fillingBatchInputModeG"
                type="radio"
                name="fillingBatchInputMode"
                checked={fillingInputMode === "g"}
                onChange={() => changeInputMode("FILLING", "g")}
              />
              g
            </label>
            <label htmlFor="fillingBatchInputModePct" style={radioLbl}>
              <input
                id="fillingBatchInputModePct"
                type="radio"
                name="fillingBatchInputMode"
                checked={fillingInputMode === "pct"}
                onChange={() => changeInputMode("FILLING", "pct")}
              />
              %
            </label>
            <button
              onClick={() => toggleBatchMode("FILLING")}
              style={{ ...btn, padding: "2px 6px", fontSize: "10px", color: "#6d28d9" }}
              title="Close batch mode"
            >
              {"✕"}
            </button>
          </div>
        )}
      </div>
      <div className="ag-theme-alpine formula-grid" style={{ width: "100%" }}>
        <AgGridReact<FormulaRow>
          rowData={fillingRows}
          columnDefs={fillingColumnDefs}
          defaultColDef={{ sortable: true, resizable: true }}
          pinnedBottomRowData={fillingPinned}
          onCellValueChanged={onCellValueChanged}
          onCellClicked={handleIngredientCellClick}
          onCellDoubleClicked={handleCellDoubleClicked}
          getRowId={(p) => p.data._rowId}
          domLayout="autoHeight"
          singleClickEdit
          stopEditingWhenCellsLoseFocus={false}
          rowHeight={28}
          headerHeight={32}
          suppressMovableColumns
          animateRows={false}
        />
      </div>

      {/* ── Process Notes ── */}
      <div>
        <div style={lbl}>{t('formulaSheet.processNotes')}</div>
        <textarea
          value={processNotes} onChange={(e) => setProcessNotes(e.target.value)}
          rows={4} placeholder={t('sheet.describeProcess')}
          style={{ ...inp, width: "100%", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
        />
      </div>

      {/* ── Observations ── */}
      <div>
        <div style={lbl}>{t('formulaSheet.observationsResults')}</div>
        <textarea
          value={observationNotes} onChange={(e) => setObservationNotes(e.target.value)}
          rows={4} placeholder={t('sheet.recordTrialResults')}
          style={{ ...inp, width: "100%", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
        />
      </div>

      {/* ── Post-Process / Ratio Settings ── */}
      <div style={{
        marginTop: "16px", padding: "14px", backgroundColor: "#f0fdfa",
        borderRadius: "8px", border: "1px solid #99f6e4",
      }}>
        <div style={{ fontSize: "11px", fontWeight: 700, color: "#134e4a", marginBottom: "12px" }}>
          {t('formulaSheet.ratioMoistureTargets')}
        </div>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-end" }}>
          {/* Dough Ratio */}
          <div>
            <div style={lbl}>{t('formulaSheet.doughPct')}</div>
            <input
              lang="en" type="number" min={0} max={100}
              value={header.dough_ratio_pct ?? ""}
              onChange={(e) => setHeader((h) => ({
                ...h,
                dough_ratio_pct: e.target.value ? Number(e.target.value) : null,
              }))}
              style={{ ...inp, width: "60px" }}
            />
          </div>
          <div>
            <div style={lbl}>{t('formulaSheet.fillingPct')}</div>
            <input
              readOnly
              value={header.dough_ratio_pct != null ? 100 - header.dough_ratio_pct : ""}
              style={{ ...roInp, width: "60px" }}
            />
          </div>
          {/* Ratio Mode */}
          <div>
            <div style={lbl}>{t('formulaSheet.ratioMode')}</div>
            <select
              value={header.ratio_mode ?? "as_mixed"}
              onChange={(e) => setHeader((h) => ({ ...h, ratio_mode: e.target.value }))}
              style={{ ...inp, width: "120px" }}
            >
              <option value="as_mixed">{t('formulaSheet.asMixed')}</option>
              <option value="finished">{t('formulaSheet.postProcess')}</option>
            </select>
          </div>
          {/* Final Moisture Mode */}
          <div>
            <div style={lbl}>{t('formulaSheet.finalMoisture')}</div>
            <select
              value={header.final_moisture_mode ?? ""}
              onChange={(e) => setHeader((h) => ({
                ...h,
                final_moisture_mode: e.target.value || null,
                // Clear values when switching mode
                ...(e.target.value === "composite" ? { final_moisture_dough: null, final_moisture_filling: null } : {}),
                ...(e.target.value === "components" ? { final_moisture_whole: null } : {}),
                ...(!e.target.value ? { final_moisture_whole: null, final_moisture_dough: null, final_moisture_filling: null } : {}),
              }))}
              style={{ ...inp, width: "140px" }}
            >
              <option value="">{t('formulaSheet.noneNoPostProcess')}</option>
              <option value="components">{t('formulaSheet.byComponent')}</option>
              <option value="composite">{t('formulaSheet.compositeWhole')}</option>
            </select>
          </div>
          {/* Moisture fields — conditional on mode */}
          {header.final_moisture_mode === "composite" && (
            <div>
              <div style={lbl}>{t('formulaSheet.targetMoisturePct')}</div>
              <input
                lang="en" type="number" step="0.1" min={0} max={100}
                value={header.final_moisture_whole ?? ""}
                onChange={(e) => setHeader((h) => ({
                  ...h,
                  final_moisture_whole: e.target.value ? Number(e.target.value) : null,
                }))}
                style={{ ...inp, width: "80px" }}
              />
            </div>
          )}
          {header.final_moisture_mode === "components" && (
            <>
              <div>
                <div style={lbl}>{t('formulaSheet.doughMoisturePct')}</div>
                <input
                  lang="en" type="number" step="0.1" min={0} max={100}
                  value={header.final_moisture_dough ?? ""}
                  onChange={(e) => setHeader((h) => ({
                    ...h,
                    final_moisture_dough: e.target.value ? Number(e.target.value) : null,
                  }))}
                  style={{ ...inp, width: "80px" }}
                />
              </div>
              <div>
                <div style={lbl}>{t('formulaSheet.fillingMoisturePct')}</div>
                <input
                  lang="en" type="number" step="0.1" min={0} max={100}
                  value={header.final_moisture_filling ?? ""}
                  onChange={(e) => setHeader((h) => ({
                    ...h,
                    final_moisture_filling: e.target.value ? Number(e.target.value) : null,
                  }))}
                  style={{ ...inp, width: "80px" }}
                />
              </div>
            </>
          )}
        </div>
        {header.final_moisture_mode && (
          <div style={{ marginTop: "8px", fontSize: "9px", color: "#0d9488" }}>
            {t('formulaSheet.moistureHelpText', { mode: header.ratio_mode === "finished" ? t('formulaSheet.finishedPostProcess') : t('formulaSheet.asMixedLabel') })}
          </div>
        )}
      </div>

      {/* ── Report Modal ── */}
      {reportExportType && (
        <FormulaReport
          header={header}
          rows={rawRows}
          ingMap={ingMap}
          exportType={reportExportType}
          onClose={() => setReportExportType(null)}
        />
      )}

      {/* ── Import Component Modal ── */}
      {importModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => setImportModal(null)}>
          <div style={{
            background: "#fff", borderRadius: "8px", padding: "20px", width: "480px", maxHeight: "80vh",
            display: "flex", flexDirection: "column", gap: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: "14px", fontWeight: 700 }}>
              {t('formulaSheet.importFromExisting', { component: importModal.targetComponent })}
            </div>
            <input
              type="text" placeholder={t('formulaSheet.searchFormulas')}
              value={importSearch} onChange={(e) => setImportSearch(e.target.value)}
              style={{ fontSize: "13px", padding: "6px 10px", border: "1px solid #d0d0d0", outline: "none" }}
              autoFocus
            />
            <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #e0e0e0" }}>
              {importFormulas
                .filter((f) => {
                  const q = importSearch.toLowerCase();
                  return !q || f.formula_code.toLowerCase().includes(q) || (f.display_name ?? "").toLowerCase().includes(q);
                })
                .map((f) => (
                  <div
                    key={f.formula_code}
                    onClick={() => {
                      setImportSelectedCode(f.formula_code);
                      loadImportPreview(f.formula_code, importSourceComponent);
                    }}
                    style={{
                      padding: "6px 10px", fontSize: "12px", cursor: "pointer",
                      background: importSelectedCode === f.formula_code ? "#ede9fe" : "transparent",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{f.formula_code}</span>
                    {f.display_name && <span style={{ color: "#666", marginLeft: "8px" }}>{f.display_name}</span>}
                  </div>
                ))}
            </div>
            {importSelectedCode && (
              <div style={{ display: "flex", gap: "8px", alignItems: "center", fontSize: "12px" }}>
                <span style={{ fontWeight: 600 }}>{t('formulaSheet.copyFromSection')}</span>
                {(["DOUGH", "FILLING"] as const).map((c) => (
                  <label key={c} style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
                    <input
                      type="radio" name="importSource" checked={importSourceComponent === c}
                      onChange={() => {
                        setImportSourceComponent(c);
                        loadImportPreview(importSelectedCode, c);
                      }}
                    />
                    {c}
                  </label>
                ))}
              </div>
            )}
            {importLoading && <div style={{ fontSize: "12px", color: "#999" }}>{t('formulaSheet.loadingEllipsis')}</div>}
            {!importLoading && importPreviewRows.length > 0 && (
              <div style={{ fontSize: "11px", border: "1px solid #e0e0e0", maxHeight: "140px", overflowY: "auto" }}>
                <div style={{ padding: "4px 8px", fontWeight: 700, background: "#f5f3ff", borderBottom: "1px solid #e0e0e0" }}>
                  {t('formulaSheet.previewCount', { count: importPreviewRows.length })}
                </div>
                {importPreviewRows.map((r) => (
                  <div key={r._rowId} style={{ padding: "3px 8px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between" }}>
                    <span>{r.common_name || r.ingredient_id || "\u2014"}</span>
                    <span style={{ color: "#666" }}>{r.grams != null ? r.grams.toFixed(1) + "g" : ""}</span>
                  </div>
                ))}
              </div>
            )}
            {!importLoading && importSelectedCode && importPreviewRows.length === 0 && (
              <div style={{ fontSize: "12px", color: "#999" }}>{t('formulaSheet.noIngredientsFound')}</div>
            )}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setImportModal(null)} style={btn}>{t('formulaSheet.cancel')}</button>
              <button
                onClick={confirmImport}
                disabled={importPreviewRows.length === 0}
                style={{
                  padding: "5px 14px", fontSize: "12px", fontWeight: 600, cursor: importPreviewRows.length === 0 ? "not-allowed" : "pointer",
                  border: "1px solid #7c3aed", background: "#7c3aed", color: "#fff",
                  opacity: importPreviewRows.length === 0 ? 0.5 : 1,
                }}
              >
                {t('formulaSheet.replaceWithIngredients', { component: importModal.targetComponent })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ingredient Picker (standalone portal, not an AG Grid editor) ── */}
      {pickerState && (
        <IngredientPicker
          ingredients={ingredients}
          position={pickerState.position}
          currentValue={
            rawRows.find((r) => r._rowId === pickerState.rowId)?.ingredient_id ?? null
          }
          onSelect={handleIngredientSelect}
          onClose={() => setPickerState(null)}
        />
      )}
    </div>
  );
}
